'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material'

import { SystemUpdateNotice } from '@/models/systemUpdateNotice'
import api from '@/shared/api'
import { ENDPOINTS } from '@/shared/constants'
import theme from '@/shared/theme/theme'
import { useAppSelector } from '@/store/hooks'
import { disconnectSocket, getSocket } from '@/socket'

const SYSTEM_UPDATE_NOTICE_EVENT = 'system.update.notice'
const APPLIED_NOTICE_STORAGE_KEY = 'system-update-notice:applied-issued-at'
const CHECK_INTERVAL_MS = 5 * 60 * 1000

export default function SystemUpdateRequiredPrompt() {
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'))
  const { user } = useAppSelector((state) => state.auth)
  const [notice, setNotice] = useState<SystemUpdateNotice>()
  const [isUpdating, setIsUpdating] = useState(false)
  const [appliedNoticeIssuedAt, setAppliedNoticeIssuedAt] = useState<
    string | null
  >()

  const shouldShow = useMemo(() => {
    if (!notice?.active || !notice.issuedAt) return false
    if (!appliedNoticeIssuedAt) return false

    return appliedNoticeIssuedAt !== notice.issuedAt
  }, [appliedNoticeIssuedAt, notice?.active, notice?.issuedAt])

  const applyNotice = useCallback((nextNotice: SystemUpdateNotice) => {
    setNotice(nextNotice)
  }, [])

  const checkNotice = useCallback(async () => {
    try {
      const response = await api.get<SystemUpdateNotice>(
        ENDPOINTS.SYSTEM_UPDATE_NOTICES.GET.CURRENT
      )
      applyNotice(response.data)
    } catch {
      //
    }
  }, [applyNotice])

  useEffect(() => {
    checkNotice()

    const interval = window.setInterval(checkNotice, CHECK_INTERVAL_MS)

    const handleFocus = () => checkNotice()
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkNotice()
      }
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkNotice])

  useEffect(() => {
    setAppliedNoticeIssuedAt(
      window.localStorage.getItem(APPLIED_NOTICE_STORAGE_KEY)
    )
  }, [])

  useEffect(() => {
    if (!notice?.active || !notice.issuedAt) return
    if (appliedNoticeIssuedAt !== null) return

    window.localStorage.setItem(APPLIED_NOTICE_STORAGE_KEY, notice.issuedAt)
    setAppliedNoticeIssuedAt(notice.issuedAt)
  }, [appliedNoticeIssuedAt, notice?.active, notice?.issuedAt])

  useEffect(() => {
    if (!user?.companyId || !user.id) return

    const socket = getSocket(user.companyId, user.id)

    socket.off(SYSTEM_UPDATE_NOTICE_EVENT, applyNotice)
    socket.on(SYSTEM_UPDATE_NOTICE_EVENT, applyNotice)

    return () => {
      socket.off(SYSTEM_UPDATE_NOTICE_EVENT, applyNotice)
      disconnectSocket()
    }
  }, [applyNotice, user?.companyId, user?.id])

  const clearBrowserCaches = async () => {
    if (!('caches' in window)) return

    const cacheNames = await window.caches.keys()
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
  }

  const refreshServiceWorkers = async () => {
    if (!('serviceWorker' in navigator)) return

    const registrations = await navigator.serviceWorker.getRegistrations()

    await Promise.all(
      registrations.map((registration) =>
        registration.update().catch(() => undefined)
      )
    )

    for (const registration of registrations) {
      registration.waiting?.postMessage({ type: 'SKIP_WAITING' })
    }

    await Promise.all(
      registrations.map((registration) =>
        registration.unregister().catch(() => false)
      )
    )
  }

  const reloadWithCacheBust = () => {
    const url = new URL(window.location.href)
    url.searchParams.set('systemUpdatedAt', Date.now().toString())
    window.location.replace(url.toString())
  }

  const handleUpdate = async () => {
    if (!notice?.issuedAt) return

    setIsUpdating(true)

    try {
      await refreshServiceWorkers()
      await clearBrowserCaches()
    } finally {
      window.localStorage.setItem(APPLIED_NOTICE_STORAGE_KEY, notice.issuedAt)
      setAppliedNoticeIssuedAt(notice.issuedAt)
      reloadWithCacheBust()
    }
  }

  if (!shouldShow || !notice) return null

  return (
    <Box
      sx={{
        position: 'fixed',
        right: { xs: 12, sm: 'auto' },
        bottom: { xs: 'calc(env(safe-area-inset-bottom) + 12px)', sm: 24 },
        left: { xs: 12, sm: 24 },
        zIndex: (muiTheme) => muiTheme.zIndex.snackbar + 20,
        pointerEvents: 'none',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: { xs: '100%', sm: 390 },
          maxWidth: '100%',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'warning.300',
          p: { xs: 2, sm: 2.25 },
          pointerEvents: 'auto',
        }}
      >
        <Typography fontWeight={700} mb={0.75}>
          {notice.title}
        </Typography>
        <Typography color="text.secondary" fontSize={14} mb={2}>
          {notice.message}
        </Typography>
        <Button
          fullWidth={isSmDown}
          variant="contained"
          color="warning"
          onClick={handleUpdate}
          disabled={isUpdating}
          startIcon={
            isUpdating ? <CircularProgress color="inherit" size={16} /> : null
          }
        >
          {isUpdating ? 'Atualizando...' : 'Atualizar agora'}
        </Button>
      </Paper>
    </Box>
  )
}
